import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

export default function Gallery() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const images = [
    '/assets/img/1fb6a28822d0.webp',
    '/assets/img/brand-activations-10.webp',
    '/assets/img/weddings-04.webp',
    '/assets/img/glamping-02.webp',
    '/assets/img/festivals-fairs-11.webp',
    '/assets/img/festival-vendor-village-27.webp',
    '/assets/img/festival-vendor-village-29.webp',
    '/assets/img/brand-activations-06.webp'
  ];

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const goToPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev - 1 + images.length) % images.length : null));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev + 1) % images.length : null));
  }, [images.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };

    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, closeLightbox, goToPrev, goToNext]);

  return (
    <section id="gallery" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Tents in Action
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            See how our stretch tents transform any event
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-xl aspect-[3/2] cursor-pointer"
              onClick={() => setLightboxIndex(index)}
            >
              <img
                src={image}
                alt={`Gallery ${index + 1}`}
                className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full backdrop-blur-sm">
                  <i className="ri-zoom-in-line text-white text-xl"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-12">
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all transform hover:scale-105 whitespace-nowrap"
          >
            <span>Explore Our Gallery</span>
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </div>

      {/* Lightbox Overlay */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-white bg-white/10 hover:bg-white/25 rounded-full transition-colors cursor-pointer z-10"
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          {/* Prev Button */}
          <button
            className="absolute left-5 w-11 h-11 flex items-center justify-center text-white bg-white/10 hover:bg-white/25 rounded-full transition-colors cursor-pointer z-10"
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          {/* Image */}
          <div
            className="relative max-w-5xl w-full mx-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[lightboxIndex]}
              alt={`Gallery ${lightboxIndex + 1}`}
              className="w-full max-h-[80vh] object-contain rounded-xl"
            />
            <p className="text-center text-white/50 text-sm mt-3">
              {lightboxIndex + 1} / {images.length}
            </p>
          </div>

          {/* Next Button */}
          <button
            className="absolute right-5 w-11 h-11 flex items-center justify-center text-white bg-white/10 hover:bg-white/25 rounded-full transition-colors cursor-pointer z-10"
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            aria-label="Next image"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>
      )}
    </section>
  );
}
